import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Coffee, Sparkles, Hash, FileText, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useSeoMeta } from "@unhead/react";
import { nip19 } from "nostr-tools";
import { detectSearchType } from "@/lib/searchUtils";
import { useProfileSearch } from "@/hooks/useProfileSearch";
import { useContentSearch } from "@/hooks/useContentSearch";
import { useHashtagSearch } from "@/hooks/useHashtagSearch";
import { useAuthor } from "@/hooks/useAuthor";
import { NoteContent } from "@/components/NoteContent";
import type { NostrEvent } from "@nostrify/nostrify";

function NoteResult({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.display_name || metadata?.name || "Anonymous";
  const npub = nip19.npubEncode(event.pubkey);

  return (
    <Card className="border-0 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all rounded-2xl">
      <CardHeader className="pb-3">
        <Link to={`/${npub}`} className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-xl">
            <AvatarImage src={metadata?.picture} />
            <AvatarFallback className="rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-sm">
              {displayName[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{displayName}</div>
            {metadata?.nip05 && (
              <div className="text-xs text-primary truncate">{metadata.nip05}</div>
            )}
          </div>
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-sm whitespace-pre-wrap break-words line-clamp-4">
          <NoteContent event={event} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  useSeoMeta({
    title: query ? `Search "${query}" - ZapMeACoffee` : "Search - ZapMeACoffee",
    description: "Find creators to support with Lightning tips.",
  });

  // Detect what type of search this is
  const detection = detectSearchType(query);

  // Run appropriate searches based on detection
  const shouldSearchProfiles = detection.type.includes('profile');
  const shouldSearchContent = detection.type.includes('text');
  const shouldSearchHashtag = detection.type.includes('hashtag');

  const {
    data: profileResults,
    isLoading: isLoadingProfiles,
  } = useProfileSearch(query, shouldSearchProfiles);

  const {
    data: contentResults,
    isLoading: isLoadingContent,
  } = useContentSearch(query, shouldSearchContent);

  const {
    data: hashtagResults,
    isLoading: isLoadingHashtag,
  } = useHashtagSearch(detection.value, shouldSearchHashtag);

  const isLoading = isLoadingProfiles || isLoadingContent || isLoadingHashtag;
  const hasResults =
    (profileResults && profileResults.length > 0) ||
    (contentResults && contentResults.length > 0) ||
    (hashtagResults && hashtagResults.length > 0);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {query && (
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight mb-2">
            {detection.type.includes('hashtag') ? (
              <span className="flex items-center gap-2">
                <Hash className="h-6 w-6 text-primary" />
                #{detection.value}
              </span>
            ) : (
              `Results for "${query}"`
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {detection.type.includes('hashtag')
              ? 'Notes tagged with this hashtag'
              : detection.type.includes('text')
              ? 'Profiles and content matching your search'
              : 'Profiles matching your search'}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="border-0 shadow-lg shadow-black/5 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-2xl" />
                  <div className="flex-1 space-y-2 min-w-0">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : hasResults ? (
        <div className="space-y-8">
          {/* Profile Results */}
          {profileResults && profileResults.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Creators ({profileResults.length})
              </h2>
              <div className="space-y-4">
                {profileResults.map((result) => {
                  const npub = nip19.npubEncode(result.pubkey);
                  const displayName =
                    result.metadata.display_name || result.metadata.name || "Anonymous";
                  const about = result.metadata.about || "";
                  const truncatedAbout =
                    about.length > 140 ? about.slice(0, 140).trim() + "..." : about;

                  return (
                    <Card
                      key={result.pubkey}
                      className="border-0 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all rounded-2xl group"
                    >
                      <CardContent className="p-5">
                        <Link to={`/${npub}`} className="flex items-center gap-4">
                          <Avatar className="h-14 w-14 rounded-2xl ring-2 ring-border/50">
                            <AvatarImage
                              src={result.metadata.picture}
                              className="object-cover"
                            />
                            <AvatarFallback className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
                              {displayName[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{displayName}</h3>
                            {result.metadata.nip05 && (
                              <p className="text-sm text-primary truncate">
                                {result.metadata.nip05}
                              </p>
                            )}
                            {truncatedAbout && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                {truncatedAbout}
                              </p>
                            )}
                          </div>
                          {result.metadata.lud16 && (
                            <Button
                              size="sm"
                              className="rounded-xl bg-primary hover:bg-primary/90 shadow-md shadow-primary/20 flex-shrink-0 opacity-90 group-hover:opacity-100 transition-all"
                            >
                              <Coffee className="h-4 w-4 mr-1.5" />
                              Tip
                            </Button>
                          )}
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Content/Hashtag Results */}
          {((contentResults && contentResults.length > 0) ||
            (hashtagResults && hashtagResults.length > 0)) && (
            <section>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                {detection.type.includes('hashtag') ? (
                  <>
                    <Hash className="h-5 w-5 text-primary" />
                    Posts (
                    {hashtagResults?.length || 0})
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5 text-primary" />
                    Content ({contentResults?.length || 0})
                  </>
                )}
              </h2>
              <div className="space-y-4">
                {(hashtagResults || contentResults || []).map((event) => (
                  <NoteResult key={event.id} event={event} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : query ? (
        <Card className="border border-dashed border-border/60 shadow-none rounded-2xl">
          <CardContent className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="font-medium mb-2">No results found for "{query}"</p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Try searching with a different name or paste an npub directly
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
